// ============================================
// AVALIAÇÃO DO DOADOR VIVO DE FÍGADO — B351 (AI_152)
// ============================================
// Aba exclusiva da modalidade `doador_figado`. Espelha o padrão da irmã
// (AbaAvaliacaoClinica): editar/salvar explícito, guarda de data futura (B296) e
// gravação via `atualizarPaciente`.
//
// ⚠️ Grava em `paciente.avaliacaoDoador`, objeto SEPARADO de `avaliacaoClinica`.
//    Isso importa porque muitos doadores foram cadastrados antes como "figado" ou
//    "ambulatorial" e depois convertidos: os dois conjuntos convivem sem colisão e
//    a conversão continua reversível, sem apagar nada.
//
// A pergunta clínica aqui é OUTRA: não é "este fígado está falindo?", é "este
// fígado pode ser dividido com segurança?". Por isso nada de MELD, Child ou
// situação especial — os campos seguem o consenso ILTS-iLDLT 2026 (corpus RAG).
import React, { useState } from "react";
import {
  HeartPulse,
  ExternalLink,
  Save,
  Edit3,
  X,
  Search,
  AlertTriangle,
} from "lucide-react";
import { usePacientes } from "../context/PacientesContext";
import { theme } from "../../../shared/styles/theme";
import { ehDataFutura, msgDataFutura, hojeISO } from "../../../shared/utils/dataNaoFutura";
import { ehDoadorFigado } from "../../../shared/utils/modalidadeAtendimento";
import {
  calcularGRWR,
  calcularRemanescente,
  calcularIMC,
  classificarGRWR,
  classificarRemanescente,
  conferirCoerenciaEnxerto,
  GRWR_MINIMO,
  REMANESCENTE_MINIMO,
} from "../../../shared/utils/volumetriaDoador";
// B351 passo 4 — as listas vivem em fonte única, compartilhadas com o catálogo da
// aba Pesquisa: o que esta aba GRAVA é exatamente o que a Pesquisa FILTRA.
import {
  ENXERTO_DOADO,
  VARIACAO_ARTERIAL,
  VARIACAO_PORTAL,
  VARIACAO_BILIAR,
  VEIAS_HEPATICAS,
  SIM_NAO,
  PARECER_PSICOSSOCIAL,
  METODO_ESTEATOSE,
  PARENTESCO_RECEPTOR,
  AUTORIZACAO_JUDICIAL,
  TIPO_SANGUINEO,
} from "../../../shared/data/opcoesDoador";

interface Props {
  pacienteId: string;
  // 🔐 B88 — espelha a regra da aba irmã: a avaliação é editável só pela equipe
  // clínica (as firestore.rules são a trava real; isto é o espelho na tela).
  somenteLeitura?: boolean;
}

const card: React.CSSProperties = {
  background: "white",
  border: `1px solid ${theme.gray[200]}`,
  borderRadius: theme.borderRadius.lg,
  padding: "20px",
  marginBottom: "16px",
};

const sectionHeaderStyle: React.CSSProperties = {
  fontSize: "15px",
  fontWeight: 700,
  color: theme.gray[800],
  fontFamily: theme.fonts.sans,
  display: "flex",
  alignItems: "center",
  gap: "8px",
  margin: "0 0 16px 0",
  paddingBottom: "8px",
  borderBottom: `2px solid ${theme.gray[200]}`,
};

const labelStyle: React.CSSProperties = {
  display: "block",
  fontSize: "13px",
  fontWeight: 600,
  color: theme.gray[700],
  marginBottom: "6px",
  fontFamily: theme.fonts.sans,
};

const fieldStyle = (editing: boolean): React.CSSProperties => ({
  width: "100%",
  padding: "8px 10px",
  border: `1px solid ${editing ? theme.gray[300] : "transparent"}`,
  borderRadius: theme.borderRadius.md,
  background: editing ? "white" : theme.gray[50],
  fontSize: "14px",
  fontFamily: theme.fonts.sans,
  color: theme.gray[800],
  boxSizing: "border-box",
});

const grid = (cols: number): React.CSSProperties => ({
  display: "grid",
  gridTemplateColumns: `repeat(auto-fit, minmax(${cols}px, 1fr))`,
  gap: "14px",
});

// Cor da baliza. Não é semáforo de decisão — é sinalização; quem decide é o
// cirurgião (ver comentário em volumetriaDoador.ts).
const corBaliza = (c: string | null) =>
  c === "adequado"
    ? { bg: "#dcfce7", fg: "#166534" }
    : c === "limitrofe"
    ? { bg: "#fef3c7", fg: "#92400e" }
    : c === "insuficiente"
    ? { bg: "#fee2e2", fg: "#991b1b" }
    : { bg: theme.gray[100], fg: theme.gray[600] };

export default function AbaAvaliacaoDoador({
  pacienteId,
  somenteLeitura = false,
}: Props) {
  const { pacientes, atualizarPaciente } = usePacientes();
  const paciente = pacientes.find((p: any) => p.id === pacienteId) as any;

  const [editing, setEditing] = useState(false);
  const [dados, setDados] = useState<any>(null);
  const [buscaReceptor, setBuscaReceptor] = useState("");

  const atual = dados || paciente?.avaliacaoDoador || {};
  const set = (campo: string, valor: any) =>
    setDados({ ...(dados || paciente?.avaliacaoDoador || {}), [campo]: valor });

  // ── Derivados (o médico digita volumes; o sistema calcula as porcentagens) ──
  // B351-FU: passamos de g para cm³ (é o que a volumetria da tomografia reporta).
  // As chaves antigas seguem sendo lidas como RESERVA para não perder o que já
  // tenha sido digitado antes da troca.
  const volTotal = Number(atual.volumeFigadoTotal ?? atual.pesoFigadoTotal);
  const volEnxerto = Number(atual.volumeEnxerto ?? atual.pesoEnxerto);
  const grwr = calcularGRWR(volEnxerto, Number(atual.pesoReceptor));
  const remanescente = calcularRemanescente(volTotal, volEnxerto);
  const imc = calcularIMC(Number(atual.peso), Number(atual.altura));
  // B355 (AI_153) — trava de coerência: o enxerto digitado bate com o território
  // declarado? Nasceu do laudo real que trocou "esquerdo" por "direito" e fez o
  // GRWR sair 0,90% quando era 1,53%. Avisa, nunca bloqueia.
  const avisoVolumetria = conferirCoerenciaEnxerto(
    atual.loboDoado,
    volTotal,
    volEnxerto
  );

  // Candidatos a receptor: exclui o próprio doador e os demais doadores — um
  // doador não recebe do outro, e oferecê-los só polui a busca.
  const candidatos = (pacientes as any[])
    .filter(
      (p) =>
        p.id !== pacienteId &&
        !ehDoadorFigado(p.tipoAtendimento) &&
        buscaReceptor.trim().length >= 2 &&
        (p.nome || "").toLowerCase().includes(buscaReceptor.trim().toLowerCase())
    )
    .slice(0, 6);

  const abrirReceptor = () => {
    if (!atual.receptorId) return;
    window.open(`${window.location.origin}/prontuario/${atual.receptorId}`, "_blank");
  };

  const handleSalvar = async () => {
    if (!dados) {
      setEditing(false);
      return;
    }
    // B296 — nenhum marco pode ter data futura (retroativo é livre).
    const marcos: Array<[string, any]> = [
      ["Data da autorização judicial", dados.autorizacaoJudicialData],
      ["Data da avaliação psicossocial", dados.psicossocialData],
      // B351-FU8 (AI_153) — a cirurgia de doação é um marco: retroativo é livre,
      // futuro não existe.
      ["Data da cirurgia de doação", dados.dataDoacao],
    ];
    const futuro = marcos.find(([, v]) => ehDataFutura(v));
    if (futuro) {
      alert(msgDataFutura(futuro[0]));
      return;
    }
    await atualizarPaciente(pacienteId, { avaliacaoDoador: dados });
    setDados(null);
    setEditing(false);
  };

  if (!paciente) return null;

  const campoTexto = (
    campo: string,
    rotulo: string,
    placeholder = "",
    tipo: "text" | "number" | "date" = "text"
  ) => (
    <div>
      <label style={labelStyle}>{rotulo}</label>
      <input
        type={tipo}
        value={atual[campo] ?? ""}
        placeholder={editing ? placeholder : ""}
        max={tipo === "date" ? hojeISO() : undefined}
        readOnly={!editing}
        onChange={(e) => set(campo, e.target.value)}
        style={fieldStyle(editing)}
      />
    </div>
  );

  const campoSelect = (campo: string, rotulo: string, opcoes: string[]) => (
    <div>
      <label style={labelStyle}>{rotulo}</label>
      <select
        value={atual[campo] ?? ""}
        disabled={!editing}
        onChange={(e) => set(campo, e.target.value)}
        style={{
          ...fieldStyle(editing),
          cursor: editing ? "pointer" : "default",
        }}
      >
        <option value="">Selecione...</option>
        {/* B351-FU: valor gravado que não esteja na lista (texto livre da versão
            anterior) vira opção, para não desaparecer da tela em silêncio. */}
        {atual[campo] && !opcoes.includes(atual[campo]) && (
          <option value={atual[campo]}>{atual[campo]}</option>
        )}
        {opcoes.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    </div>
  );

  return (
    <div style={{ fontFamily: theme.fonts.sans }}>
      {/* CABEÇALHO + AÇÕES */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "16px",
        }}
      >
        <h2 style={{ ...sectionHeaderStyle, border: "none", margin: 0 }}>
          <HeartPulse size={18} color="#14b8a6" /> Avaliação do Doador
        </h2>
        <div style={{ display: "flex", gap: "8px" }}>
          {editing ? (
            <>
              <button
                onClick={() => {
                  setDados(null);
                  setEditing(false);
                }}
                style={{
                  padding: "8px 14px",
                  border: `1px solid ${theme.gray[300]}`,
                  borderRadius: theme.borderRadius.md,
                  background: "white",
                  cursor: "pointer",
                  fontSize: "13px",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                }}
              >
                <X size={14} /> Cancelar
              </button>
              <button
                onClick={handleSalvar}
                style={{
                  padding: "8px 14px",
                  border: "none",
                  borderRadius: theme.borderRadius.md,
                  background: "#14b8a6",
                  color: "white",
                  cursor: "pointer",
                  fontSize: "13px",
                  fontWeight: 600,
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                }}
              >
                <Save size={14} /> Salvar
              </button>
            </>
          ) : somenteLeitura ? null : (
            <button
              onClick={() => setEditing(true)}
              style={{
                padding: "8px 14px",
                border: `1px solid #14b8a6`,
                borderRadius: theme.borderRadius.md,
                background: "white",
                color: "#0f766e",
                cursor: "pointer",
                fontSize: "13px",
                fontWeight: 600,
                display: "flex",
                alignItems: "center",
                gap: "6px",
              }}
            >
              <Edit3 size={14} /> Editar
            </button>
          )}
        </div>
      </div>

      {/* 1. VÍNCULO COM O RECEPTOR */}
      <div style={card}>
        <h3 style={sectionHeaderStyle}>Vínculo com o receptor</h3>
        <div style={{ marginBottom: "14px" }}>
          <label style={labelStyle}>Receptor</label>
          {atual.receptorNome ? (
            <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
              <div style={{ ...fieldStyle(false), flex: 1 }}>
                {atual.receptorNome}
              </div>
              <button
                onClick={abrirReceptor}
                disabled={!atual.receptorId}
                title="Abrir o prontuário do receptor em nova aba"
                style={{
                  padding: "8px 12px",
                  border: `1px solid ${theme.primary[600]}`,
                  borderRadius: theme.borderRadius.md,
                  background: "white",
                  color: theme.primary[600],
                  cursor: atual.receptorId ? "pointer" : "not-allowed",
                  fontSize: "13px",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  whiteSpace: "nowrap",
                }}
              >
                <ExternalLink size={14} /> Abrir prontuário
              </button>
              {editing && (
                <button
                  onClick={() => {
                    set("receptorId", "");
                    set("receptorNome", "");
                  }}
                  style={{
                    padding: "8px 10px",
                    border: `1px solid ${theme.gray[300]}`,
                    borderRadius: theme.borderRadius.md,
                    background: "white",
                    cursor: "pointer",
                    fontSize: "13px",
                  }}
                >
                  Trocar
                </button>
              )}
            </div>
          ) : editing ? (
            <div style={{ position: "relative" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                <Search size={14} color={theme.gray[500]} />
                <input
                  value={buscaReceptor}
                  onChange={(e) => setBuscaReceptor(e.target.value)}
                  placeholder="Digite o nome do receptor já cadastrado..."
                  style={fieldStyle(true)}
                />
              </div>
              {candidatos.length > 0 && (
                <div
                  style={{
                    border: `1px solid ${theme.gray[200]}`,
                    borderRadius: theme.borderRadius.md,
                    marginTop: "4px",
                    background: "white",
                  }}
                >
                  {candidatos.map((c) => (
                    <div
                      key={c.id}
                      onClick={() => {
                        const base = dados || paciente?.avaliacaoDoador || {};
                        setDados({
                          ...base,
                          receptorId: c.id,
                          receptorNome: c.nome,
                        });
                        setBuscaReceptor("");
                      }}
                      style={{
                        padding: "8px 10px",
                        cursor: "pointer",
                        fontSize: "13px",
                        borderBottom: `1px solid ${theme.gray[100]}`,
                      }}
                    >
                      {c.nome}
                    </div>
                  ))}
                </div>
              )}
              {buscaReceptor.trim().length >= 2 && candidatos.length === 0 && (
                <div
                  style={{
                    fontSize: "12px",
                    color: theme.gray[500],
                    marginTop: "6px",
                  }}
                >
                  Nenhum paciente encontrado com esse nome. O receptor precisa
                  estar cadastrado.
                </div>
              )}
            </div>
          ) : (
            <div style={{ ...fieldStyle(false), color: theme.gray[500] }}>
              Não informado
            </div>
          )}
        </div>
        <div style={grid(220)}>
          {campoSelect("parentesco", "Parentesco com o receptor", PARENTESCO_RECEPTOR)}
          {campoSelect("autorizacaoJudicial", "Autorização judicial", AUTORIZACAO_JUDICIAL)}
          {campoTexto("autorizacaoJudicialData", "Data da autorização", "", "date")}
        </div>
        <p
          style={{
            fontSize: "12px",
            color: theme.gray[500],
            margin: "10px 0 0 0",
          }}
        >
          Lei 9.434/1997, art. 9º: a doação dispensa autorização judicial apenas
          para cônjuge ou parente consanguíneo até o 4º grau.
        </p>

        {/* B351-FU8 (AI_153) — ESTÁGIO: a doação já aconteceu?
            Achado da 1ª validação da análise geral: o prontuário era de uma
            doadora que JÁ havia doado (05/08/2024), e o sistema não tinha como
            saber. A IA descobriu sozinha lendo um laudo de seguimento e chamou a
            pergunta de "prejudicada" — bom comportamento, mas o texto inteiro foi
            gasto numa decisão que não existia mais. Sem este campo, todo doador
            que já doou seguiria sendo analisado como candidato. */}
        <div style={{ ...grid(220), marginTop: "14px" }}>
          {campoSelect("doacaoRealizada", "Doação já realizada?", SIM_NAO)}
          {atual.doacaoRealizada === "Sim" &&
            campoTexto("dataDoacao", "Data da cirurgia de doação", "", "date")}
        </div>
        {atual.doacaoRealizada === "Sim" && (
          <p
            style={{
              fontSize: "12px",
              color: theme.gray[600],
              margin: "8px 0 0 0",
            }}
          >
            Com a doação realizada, a análise de IA passa a ser de{" "}
            <strong>seguimento do doador</strong> — deixa de discutir aptidão
            para doar.
          </p>
        )}
      </div>

      {/* 2. DADOS DO DOADOR */}
      <div style={card}>
        <h3 style={sectionHeaderStyle}>Dados do doador</h3>
        <div style={grid(180)}>
          {campoTexto("peso", "Peso (kg)", "70", "number")}
          {campoTexto("altura", "Altura (cm ou m)", "175", "number")}
          <div>
            <label style={labelStyle}>IMC (calculado)</label>
            <div style={{ ...fieldStyle(false), fontWeight: 600 }}>
              {imc !== null ? `${imc} kg/m²` : "—"}
            </div>
          </div>
          {campoSelect("tipoSanguineo", "Tipo sanguíneo", TIPO_SANGUINEO)}
        </div>
        <div style={{ marginTop: "14px" }}>
          <label style={labelStyle}>Comorbidades do doador</label>
          <textarea
            value={atual.comorbidades ?? ""}
            readOnly={!editing}
            onChange={(e) => set("comorbidades", e.target.value)}
            rows={3}
            style={{ ...fieldStyle(editing), resize: "vertical" }}
          />
        </div>
      </div>

      {/* 3. TROMBOFILIA — B351-FU (AI_152): subiu para logo depois dos dados do
          doador, a pedido de Leonardo. Faz sentido na ordem de raciocínio: é
          rastreio de APTIDÃO do doador (o consenso ILTS-iLDLT trata o workup
          procoagulante junto da aceitabilidade), enquanto volumetria e anatomia
          já são planejamento do enxerto. */}
      <div style={card}>
        <h3 style={sectionHeaderStyle}>Rastreio de trombofilia</h3>
        <div style={grid(200)}>
          {campoTexto("fatorVLeiden", "Fator V Leiden")}
          {campoTexto("protrombinaG20210A", "Protrombina G20210A")}
          {campoTexto("proteinaC", "Proteína C")}
          {campoTexto("proteinaS", "Proteína S")}
          {campoTexto("antitrombina3", "Antitrombina III")}
          {campoTexto("anticardiolipina", "Anticardiolipina")}
          {campoTexto("antiB2Glicoproteina", "Anti-β2-glicoproteína I")}
          {campoTexto("anticoagulanteLupico", "Anticoagulante lúpico")}
        </div>
      </div>

      {/* 4. VOLUMETRIA */}
      <div style={card}>
        <h3 style={sectionHeaderStyle}>Volumetria e enxerto</h3>
        <div style={grid(180)}>
          {campoSelect("loboDoado", "Enxerto a ser doado", ENXERTO_DOADO)}
          {campoTexto("volumeFigadoTotal", "Fígado total (cm³)", "1500", "number")}
          {campoTexto("volumeEnxerto", "Enxerto (cm³)", "800", "number")}
          {campoTexto("pesoReceptor", "Peso do receptor (kg)", "70", "number")}
        </div>
        <div style={{ ...grid(220), marginTop: "16px" }}>
          <div>
            <label style={labelStyle}>GRWR (calculado)</label>
            <div
              style={{
                ...fieldStyle(false),
                fontWeight: 700,
                background: corBaliza(classificarGRWR(grwr)).bg,
                color: corBaliza(classificarGRWR(grwr)).fg,
              }}
            >
              {grwr !== null ? `${grwr}%` : "—"}
            </div>
            <p style={{ fontSize: "11px", color: theme.gray[500], margin: "4px 0 0 0" }}>
              Enxerto ÷ peso do receptor, assumindo 1 cm³ ≈ 1 g. Baliza usual: ≥{" "}
              {GRWR_MINIMO}%.
            </p>
          </div>
          <div>
            <label style={labelStyle}>Remanescente do doador (calculado)</label>
            <div
              style={{
                ...fieldStyle(false),
                fontWeight: 700,
                background: corBaliza(classificarRemanescente(remanescente)).bg,
                color: corBaliza(classificarRemanescente(remanescente)).fg,
              }}
            >
              {remanescente !== null ? `${remanescente}%` : "—"}
            </div>
            <p style={{ fontSize: "11px", color: theme.gray[500], margin: "4px 0 0 0" }}>
              O que sobra para quem doa. Baliza usual: ≥ {REMANESCENTE_MINIMO}%.
            </p>
          </div>
        </div>
        {/* B355 (AI_153) — aviso de coerência. Fica ABAIXO do GRWR e do
            remanescente de propósito: é justamente esses dois números que um
            enxerto mal rotulado corrompe. */}
        {avisoVolumetria && (
          <div
            style={{
              marginTop: "16px",
              padding: "10px 12px",
              background: "#fffbeb",
              border: "1px solid #fcd34d",
              borderRadius: theme.borderRadius.md,
              display: "flex",
              gap: "8px",
              alignItems: "flex-start",
            }}
          >
            <AlertTriangle
              size={16}
              color="#b45309"
              style={{ flexShrink: 0, marginTop: "1px" }}
            />
            <p
              style={{
                margin: 0,
                fontSize: "12px",
                lineHeight: 1.5,
                color: "#92400e",
                fontFamily: theme.fonts.sans,
              }}
            >
              {avisoVolumetria.mensagem}
            </p>
          </div>
        )}
      </div>

      {/* 5. ANATOMIA */}
      <div style={card}>
        <h3 style={sectionHeaderStyle}>Anatomia</h3>
        {/* B351-FU (AI_152) — listas em vez de texto livre, a pedido de Leonardo:
            campo estruturado é o que permite analisar a casuística depois. Cada
            eixo abre com o HABITUAL e segue pelas variações mais frequentes na
            literatura de doador vivo. "Outra" + o campo de detalhe abaixo evitam
            a perda de informação quando a realidade não couber na lista. */}
        <div style={grid(240)}>
          {campoSelect("variacaoArterial", "Artéria hepática (Michels)", VARIACAO_ARTERIAL)}
          {campoSelect("variacaoPortal", "Veia porta", VARIACAO_PORTAL)}
          {campoSelect("variacaoBiliar", "Via biliar", VARIACAO_BILIAR)}
          {campoSelect("veiasHepaticas", "Veias hepáticas — padrão", VEIAS_HEPATICAS)}
          {campoSelect("tributariaV5", "Tributária de V5 significativa?", SIM_NAO)}
          {campoSelect("tributariaV8", "Tributária de V8 significativa?", SIM_NAO)}
          {campoSelect("incluiVeiaHepaticaMedia", "Inclui a veia hepática média?", SIM_NAO)}
        </div>
        <div style={{ marginTop: "14px" }}>
          <label style={labelStyle}>Detalhe anatômico (texto livre)</label>
          <textarea
            value={atual.anatomiaDetalhe ?? ""}
            readOnly={!editing}
            onChange={(e) => set("anatomiaDetalhe", e.target.value)}
            rows={2}
            placeholder={
              editing
                ? "O que a lista não cobre: calibre, distância da confluência, achados do laudo..."
                : ""
            }
            style={{ ...fieldStyle(editing), resize: "vertical" }}
          />
        </div>
      </div>

      {/* 6. ESTEATOSE */}
      <div style={card}>
        <h3 style={sectionHeaderStyle}>Esteatose hepática</h3>
        <div style={grid(200)}>
          {campoTexto("esteatosePercentual", "Esteatose (%)", "10", "number")}
          {campoSelect("esteatoseMetodo", "Método", METODO_ESTEATOSE)}
        </div>
      </div>

      {/* 7. PSICOSSOCIAL */}
      <div style={card}>
        <h3 style={sectionHeaderStyle}>Avaliação psicossocial</h3>
        <div style={grid(220)}>
          {campoSelect("psicossocialParecer", "Parecer", PARECER_PSICOSSOCIAL)}
          {campoTexto("psicossocialData", "Data da avaliação", "", "date")}
        </div>
        <div style={{ marginTop: "14px" }}>
          <label style={labelStyle}>Observações da avaliação psicossocial</label>
          <textarea
            value={atual.psicossocialObs ?? ""}
            readOnly={!editing}
            onChange={(e) => set("psicossocialObs", e.target.value)}
            rows={3}
            style={{ ...fieldStyle(editing), resize: "vertical" }}
          />
        </div>
      </div>

      {/* 8. OBSERVAÇÕES */}
      <div style={card}>
        <h3 style={sectionHeaderStyle}>Observações gerais</h3>
        <textarea
          value={atual.observacoes ?? ""}
          readOnly={!editing}
          onChange={(e) => set("observacoes", e.target.value)}
          rows={4}
          style={{ ...fieldStyle(editing), resize: "vertical" }}
        />
      </div>
    </div>
  );
}
